/**
 * Email and phone validation utilities.
 * Security Level 1: Basic format validation, disposable email blocking, typo detection.
 */

// ============================================================================
// Email Validation
// ============================================================================

/**
 * RFC 5322-compliant email regex (simplified but robust)
 */
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Disposable/temporary email domains to block
 * This list covers the most common disposable email services
 */
const DISPOSABLE_EMAIL_DOMAINS = new Set([
  // Popular disposable services
  '10minutemail.com',
  '10minutemail.net',
  'guerrillamail.com',
  'guerrillamail.org',
  'guerrillamail.net',
  'guerrillamailblock.com',
  'tempmail.com',
  'temp-mail.org',
  'temp-mail.io',
  'throwaway.email',
  'throwawaymail.com',
  'mailinator.com',
  'mailinator2.com',
  'mailinater.com',
  'maildrop.cc',
  'getairmail.com',
  'getnada.com',
  'yopmail.com',
  'yopmail.fr',
  'dispostable.com',
  'mailnesia.com',
  'fakeinbox.com',
  'sharklasers.com',
  'trashmail.com',
  'trashmail.net',
  'trash-mail.com',
  'mytrashmail.com',
  'mt2015.com',
  'tempinbox.com',
  'discard.email',
  'discardmail.com',
  'spamgourmet.com',
  'spamcowboy.com',
  'mailcatch.com',
  'meltmail.com',
  'mintemail.com',
  'emailondeck.com',
  'anonymbox.com',
  'mohmal.com',
  'emailfake.com',
  'fakemailgenerator.com',
  'tempmailaddress.com',
  'burnermail.io',
  'inboxkitten.com',
  'dropmail.me',
  'instantemailaddress.com',
  'emailtemporar.ro',
  'crazymailing.com',
  'tempail.com',
  'harakirimail.com',
  'mailsac.com',
  'jetable.org',
  'spambox.us',
  'spamfree24.org',
  'mailexpire.com',
  'tempsky.com',
  'deadaddress.com',
  'sofimail.com',
  'trbvm.com',
  '20minutemail.com',
  '33mail.com',
  'maildrop.cc',
  'mailnull.com',
  'spam4.me',
  'throwam.com',
  // Russian disposable services
  'mailforspam.com',
  'temp-mail.ru',
  // German disposable services
  'wegwerfmail.de',
  'wegwerfmail.net',
  'einrot.com',
  // Catch-all patterns (subdomains of these are also disposable)
  'tempmail.ninja',
  'guerrillamail.biz',
  'guerrillamail.de',
  'sharklasers.com',
  'spam.la',
  'grr.la',
]);

/**
 * Common email domain typos and their corrections
 */
const EMAIL_TYPO_CORRECTIONS: Record<string, string> = {
  // Gmail typos
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmali.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmil.com': 'gmail.com',
  'gmaul.com': 'gmail.com',
  'gemail.com': 'gmail.com',
  'gimail.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.om': 'gmail.com',
  'gmail.cim': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.vom': 'gmail.com',
  'gmail.comm': 'gmail.com',
  'gmail.coм': 'gmail.com', // Cyrillic м
  // Outlook/Hotmail typos
  'hotmial.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmali.com': 'hotmail.com',
  'hotmaill.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.cm': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'hotamil.com': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'outlook.cm': 'outlook.com',
  'outlookcom': 'outlook.com',
  // Yahoo typos
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yaoo.com': 'yahoo.com',
  'yhoo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yahoo.cm': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  // iCloud typos
  'iclod.com': 'icloud.com',
  'icoud.com': 'icloud.com',
  'icloud.co': 'icloud.com',
  // Common TLD typos
  '.con': '.com',
  '.cmo': '.com',
  '.cm': '.com',
  '.co': '.com', // This one is tricky - .co is valid
  '.vom': '.com',
  '.coom': '.com',
  '.comm': '.com',
  '.ent': '.net',
  '.nte': '.net',
  '.ogr': '.org',
  '.orgg': '.org',
};

export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
  suggestion?: string;
  isDisposable?: boolean;
}

/**
 * Validate email address with comprehensive checks
 */
export function validateEmail(email: string): EmailValidationResult {
  const trimmedEmail = email.trim().toLowerCase();

  // Empty check
  if (!trimmedEmail) {
    return { isValid: false, error: 'Email is required' };
  }

  // Basic format check
  if (!EMAIL_REGEX.test(trimmedEmail)) {
    return { isValid: false, error: 'Invalid email format' };
  }

  // Extract domain
  const atIndex = trimmedEmail.lastIndexOf('@');
  if (atIndex === -1) {
    return { isValid: false, error: 'Invalid email format' };
  }
  const domain = trimmedEmail.substring(atIndex + 1);

  // Check for disposable email domains
  if (DISPOSABLE_EMAIL_DOMAINS.has(domain)) {
    return {
      isValid: false,
      error: 'Please use a permanent email address',
      isDisposable: true,
    };
  }

  // Check for common typos and suggest correction
  if (EMAIL_TYPO_CORRECTIONS[domain]) {
    const correctedDomain = EMAIL_TYPO_CORRECTIONS[domain];
    const localPart = trimmedEmail.substring(0, atIndex);
    return {
      isValid: false,
      error: `Did you mean ${correctedDomain}?`,
      suggestion: `${localPart}@${correctedDomain}`,
    };
  }

  // Check for TLD-only typos (e.g., .con instead of .com)
  for (const [typo, correction] of Object.entries(EMAIL_TYPO_CORRECTIONS)) {
    if (typo.startsWith('.') && domain.endsWith(typo.substring(1))) {
      // Skip .co check as it's a valid TLD
      if (typo === '.co' && domain.length > 3) continue;

      const correctedDomain = domain.slice(0, -typo.length + 1) + correction.substring(1);
      const localPart = trimmedEmail.substring(0, atIndex);
      return {
        isValid: false,
        error: `Did you mean ${correctedDomain}?`,
        suggestion: `${localPart}@${correctedDomain}`,
      };
    }
  }

  // Domain minimum length check (at least x.xx)
  if (domain.length < 4 || !domain.includes('.')) {
    return { isValid: false, error: 'Invalid email domain' };
  }

  // TLD check (at least 2 characters after last dot)
  const tld = domain.split('.').pop();
  if (!tld || tld.length < 2) {
    return { isValid: false, error: 'Invalid email domain' };
  }

  return { isValid: true };
}

// ============================================================================
// Phone Validation
// ============================================================================

/**
 * Country phone configurations
 * Format: length = expected digits after country code
 */
interface PhoneConfig {
  prefix: string;
  lengths: number[];  // Valid lengths (some countries have variable length)
  format?: string;    // Display format hint
}

const PHONE_CONFIGS: Record<string, PhoneConfig> = {
  US: { prefix: '+1', lengths: [10], format: '(XXX) XXX-XXXX' },
  CA: { prefix: '+1', lengths: [10], format: '(XXX) XXX-XXXX' },
  GB: { prefix: '+44', lengths: [10, 11], format: 'XXXX XXXXXX' },
  DE: { prefix: '+49', lengths: [10, 11, 12], format: 'XXX XXXXXXXX' },
  FR: { prefix: '+33', lengths: [9], format: 'X XX XX XX XX' },
  IT: { prefix: '+39', lengths: [9, 10], format: 'XXX XXX XXXX' },
  ES: { prefix: '+34', lengths: [9], format: 'XXX XXX XXX' },
  NL: { prefix: '+31', lengths: [9], format: 'XX XXX XXXX' },
  BE: { prefix: '+32', lengths: [8, 9], format: 'XXX XX XX XX' },
  AT: { prefix: '+43', lengths: [10, 11], format: 'XXX XXXXXXX' },
  CH: { prefix: '+41', lengths: [9], format: 'XX XXX XX XX' },
  PL: { prefix: '+48', lengths: [9], format: 'XXX XXX XXX' },
  CZ: { prefix: '+420', lengths: [9], format: 'XXX XXX XXX' },
  SK: { prefix: '+421', lengths: [9], format: 'XXX XXX XXX' },
  AU: { prefix: '+61', lengths: [9], format: 'XXX XXX XXX' },
  NZ: { prefix: '+64', lengths: [8, 9, 10], format: 'XX XXX XXXX' },
  JP: { prefix: '+81', lengths: [10, 11], format: 'XX-XXXX-XXXX' },
  KR: { prefix: '+82', lengths: [9, 10], format: 'XX-XXXX-XXXX' },
  CN: { prefix: '+86', lengths: [11], format: 'XXX XXXX XXXX' },
  IN: { prefix: '+91', lengths: [10], format: 'XXXXX XXXXX' },
  BR: { prefix: '+55', lengths: [10, 11], format: '(XX) XXXXX-XXXX' },
  MX: { prefix: '+52', lengths: [10], format: 'XX XXXX XXXX' },
  RU: { prefix: '+7', lengths: [10], format: 'XXX XXX XX XX' },
  ZA: { prefix: '+27', lengths: [9], format: 'XX XXX XXXX' },
  AE: { prefix: '+971', lengths: [9], format: 'XX XXX XXXX' },
  SA: { prefix: '+966', lengths: [9], format: 'XX XXX XXXX' },
  SG: { prefix: '+65', lengths: [8], format: 'XXXX XXXX' },
  HK: { prefix: '+852', lengths: [8], format: 'XXXX XXXX' },
  TW: { prefix: '+886', lengths: [9], format: 'XXX XXX XXX' },
  SE: { prefix: '+46', lengths: [9], format: 'XX XXX XX XX' },
  NO: { prefix: '+47', lengths: [8], format: 'XXX XX XXX' },
  DK: { prefix: '+45', lengths: [8], format: 'XX XX XX XX' },
  FI: { prefix: '+358', lengths: [9, 10], format: 'XX XXX XXXX' },
  IE: { prefix: '+353', lengths: [9], format: 'XX XXX XXXX' },
  PT: { prefix: '+351', lengths: [9], format: 'XXX XXX XXX' },
  GR: { prefix: '+30', lengths: [10], format: 'XXX XXX XXXX' },
  HU: { prefix: '+36', lengths: [9], format: 'XX XXX XXXX' },
  RO: { prefix: '+40', lengths: [9], format: 'XXX XXX XXX' },
  IL: { prefix: '+972', lengths: [9], format: 'XX XXX XXXX' },
  TR: { prefix: '+90', lengths: [10], format: 'XXX XXX XXXX' },
  // Default for unknown countries
  DEFAULT: { prefix: '+', lengths: [7, 8, 9, 10, 11, 12, 13, 14, 15] },
};

export interface PhoneValidationResult {
  isValid: boolean;
  error?: string;
  formatted?: string;
  digits?: string;
}

/**
 * Extract only digits from phone input
 */
function extractDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}

/**
 * Validate phone number for a given country
 */
export function validatePhone(phone: string, countryCode: string): PhoneValidationResult {
  const digits = extractDigits(phone);

  // Empty is valid (phone is optional)
  if (!digits) {
    return { isValid: true, digits: '' };
  }

  // Get country config
  const config = PHONE_CONFIGS[countryCode] || PHONE_CONFIGS.DEFAULT;

  // Check length
  if (!config.lengths.includes(digits.length)) {
    const expectedLengths = config.lengths.join(' or ');
    return {
      isValid: false,
      error: `Phone should be ${expectedLengths} digits`,
      digits,
    };
  }

  // Format the number for display
  const formatted = `${config.prefix} ${digits}`;

  return {
    isValid: true,
    formatted,
    digits,
  };
}

/**
 * Get phone prefix for a country
 */
export function getPhonePrefix(countryCode: string): string {
  return PHONE_CONFIGS[countryCode]?.prefix || PHONE_CONFIGS.DEFAULT.prefix;
}

/**
 * Get expected phone length hint for a country
 */
export function getPhoneLengthHint(countryCode: string): string {
  const config = PHONE_CONFIGS[countryCode];
  if (!config) return '';

  if (config.lengths.length === 1) {
    return `${config.lengths[0]} digits`;
  }
  return `${Math.min(...config.lengths)}-${Math.max(...config.lengths)} digits`;
}

// ============================================================================
// Combined Validation
// ============================================================================

export interface ContactValidationResult {
  email: EmailValidationResult;
  phone: PhoneValidationResult;
  isValid: boolean;
}

/**
 * Validate both email and phone together
 */
export function validateContact(
  email: string,
  phone: string,
  countryCode: string
): ContactValidationResult {
  const emailResult = validateEmail(email);
  const phoneResult = validatePhone(phone, countryCode);

  return {
    email: emailResult,
    phone: phoneResult,
    isValid: emailResult.isValid && phoneResult.isValid,
  };
}
